/**
 * Template Renderer for report_template_v3.html
 * Supports reporte_integral with multiple scenarios
 * 100% client-side compatible (no fs/path imports)
 * UTF-8 enforced via standard DOM operations
 */

// Import template as string (Vite handles ?raw imports)
import templateContent from '../../templates/report_template_v3.html?raw'
import { VALOR_UPDP_ACTUAL } from '../core/engine/tipos.js'
import { toCurrency } from './numberUtils.js'

const STYLES_CAJA = {
  SIPA: { bg: '#003366', text: 'NACIONAL', label: 'SIPA - SISTEMA NACIONAL' },
  IPS: { bg: '#2e7d32', text: 'PROVINCIAL', label: 'IPS - PROV. BUENOS AIRES' },
  'Caja-CBA': { bg: '#c62828', text: 'PROVINCIAL', label: 'CAJA JUBILACIONES CORDOBA' },
  'Santa Fe': { bg: '#ef6c00', text: 'PROVINCIAL', label: 'CAJA PREVISION SANTA FE' },
  OTRA: { bg: '#455a64', text: 'OTRA CAJA', label: 'REGIMEN DE RECIPROCIDAD' }
}

/**
 * Main function: Generate HTML from reporte_integral or legacy format
 * @param {Object} datos - Can be { juridico, economico, historiaLaboral } OR { reporteIntegral }
 * @returns {String} - HTML string ready for PDF generation
 */
export function generarReporteHTML(datos) {
  try {
    const hashRapido = (input) => {
      const str = typeof input === 'string' ? input : JSON.stringify(input || {})
      let hash = 5381
      for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) + hash) + str.charCodeAt(i)
        hash = hash & 0xffffffff
      }
      return `H${Math.abs(hash).toString(16).toUpperCase()}`
    }
    // DETERMINAR FORMATO: Nuevo reporte_integral o legacy
    const reporteIntegral = datos.reporteIntegral || datos.reporte_integral || null
    const juridico = datos.juridico || reporteIntegral?.escenarios_calculados?.[0]?.haber_desglosado || null
    const economico = datos.economico || reporteIntegral?.escenarios_calculados?.[0]?.haber_desglosado || null
    const historiaLaboral = datos.historiaLaboral || datos.historia || null
    const auditoriaCalculo = datos.auditoriaCalculo || null
    const remuneracionesTabla = datos.remuneraciones || []
    const analisisMulticaja = juridico?.analisisMulticaja || null

    if (!datos.coinsConsumed) {
      console.error('Intento de descarga sin consumo de coins')
      return ''
    }

    if (!juridico && !reporteIntegral) {
      console.error('Datos insuficientes para generar reporte HTML')
      return ''
    }

    // USAR TEMPLATE IMPORTADO POR VITE
    let template = templateContent || ''
    
    if (!template) {
      console.warn('Template v3 not found, using legacy mode')
      return generarHTMLLegacy(datos)
    }

    // 1. DATOS IDENTIFICATORIOS
    const titularBackend = String(datos.titular || datos.titularValidado || '').trim()
    let nombre = titularBackend || 'No detectado'
    if (!titularBackend && historiaLaboral?.datosPersonales?.apellidoNombre) {
      nombre = historiaLaboral.datosPersonales.apellidoNombre
    } else if (!titularBackend && historiaLaboral?.apellido) {
      nombre = `${historiaLaboral.apellido}, ${historiaLaboral?.nombre || ''}`
    }

    let cuil = historiaLaboral?.datosPersonales?.cuil || 
               historiaLaboral?.cuil || 
               'Pendiente de Validación'
    
    let dni = 'Pendiente de Validación'
    if (cuil && cuil !== 'Pendiente de Validación') {
      const dniMatch = cuil.match(/(\d{8})/)
      if (dniMatch) dni = dniMatch[1]
    }

    const fechaCalculo = new Date().toLocaleDateString('es-AR')
    
    // Fecha Cese from reporte_integral or legacy
    let fechaCese = 'Pendiente de Validación'
    if (reporteIntegral?.escenarios_calculados?.[0]?.fecha_cese) {
      fechaCese = reporteIntegral.escenarios_calculados[0].fecha_cese
    } else if (historiaLaboral?.periodos?.length > 0) {
      const sorted = [...historiaLaboral.periodos].sort((a, b) => 
        new Date(b.fechaHasta) - new Date(a.fechaHasta)
      )
      const last = sorted[0]
      if (last?.fechaHasta) {
        fechaCese = new Date(last.fechaHasta).toLocaleDateString('es-AR')
      }
    }

    // Edad cierre
    let edadCierre = juridico?.edadAlCese || juridico?.edadActual || 0
    if (fechaCese !== 'Pendiente de Validación' && historiaLaboral?.fechaNacimiento) {
      const nac = new Date(historiaLaboral.fechaNacimiento)
      const cese = new Date(fechaCese.split('/').reverse().join('-'))
      if (!isNaN(nac.getTime()) && !isNaN(cese.getTime())) {
        edadCierre = Math.floor((cese - nac) / (365.25 * 24 * 60 * 60 * 1000))
      }
    }

    // 2. COMPONENTES ECONÓMICOS (from first scenario or legacy)
    const pbu = economico?.pbu || 0
    const pc = economico?.pc || 0
    const pap = economico?.pap || 0
    const haberTotal = economico?.haberFinal || economico?.haberTotal || 
                      reporteIntegral?.escenarios_calculados?.[0]?.haber_mensual || 0
    const promedio120 = economico?.promedioRemunerativo || 0
    const anosPre94 = economico?.anosPre1994 || 0
    const anosPost94 = economico?.anosPost1994 || 0
    const totalAnos = juridico?.aportesTotales || 
                     reporteIntegral?.escenarios_calculados?.[0]?.edad_evaluada || '—'
    const tasaSustitucion = economico?.tasaSustitucion || 
                            reporteIntegral?.escenarios_calculados?.[0]?.tasa_sustitucion?.tasaSustitucion || 0

    // Value-only formatter (no $ prefix) for template placeholders with existing $ in markup
    const fmtVal = (val) => {
      if (val === undefined || val === null || isNaN(val)) return '-'
      return Number(val).toLocaleString('es-AR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    }
    const fmtPct = (val) => {
      if (val === undefined || val === null || isNaN(val)) return '-'
      return `${(Number(val) * 100).toFixed(1)}%`
    }
    const obtenerTasa = (esc) => esc.haber?.tasaSustitucion ?? esc.diagnostico?.tasaSustitucion ?? 0

    // 3. NUEVO: TABLA COMPARATIVA DE ESCENARIOS
    const escs = reporteIntegral?.escenarios_calculados || []
    const escenarioRecomendado = reporteIntegral?.resumen_ejecutivo?.recomendacion || ''
    const escenarioRecomendadoId = reporteIntegral?.resumen_ejecutivo?.mejor_escenario_id || ''
    let tablaEscenariosHTML = ''
    if (escs.length > 0) {
      const netos = escs.map(e => Number(e.haber_neto ?? e.haber_mensual ?? 0))
      const maxNeto = Math.max(...netos, 0)

      tablaEscenariosHTML = `
        <div class="section">
          <h2 class="section-title">TABLA COMPARATIVA DE ESCENARIOS</h2>
          <table>
            <thead>
              <tr>
                <th>Concepto</th>
                ${escs.map(esc => `
                  <th style="text-align: center;">
                    ${esc.nombre}
                    ${(escenarioRecomendadoId && (escenarioRecomendadoId === esc.id || escenarioRecomendadoId === esc.nombre)) ? '<br><span style="display:inline-block;margin-top:4px;padding:2px 6px;background:#e6f4ea;border-left:5px solid #34a853;color:#137333;font-size:10px;font-weight:700;">✓ OPCION MAS CONVENIENTE</span>' : ''}
                    ${(esc.diagnostico?.alerta) ? ' <span title="Tasa con riesgo de confiscatoriedad">⚠️</span>' : ''}
                  </th>
                `).join('')}
              </tr>
            </thead>
            <tbody>
              <tr style="background-color: #f1f2f6;">
                <td style="font-weight: bold;">Régimen (edad)</td>
                ${escs.map(esc => {
                  const cumple = esc.regimen && esc.regimen !== 'NoAcceso'
                  return `
                    <td style="text-align: center; color: ${cumple ? '#16a34a' : '#dc2626'};">
                      <strong>${esc.regimen || 'NoAcceso'}</strong>
                      <br><span style="font-size: 0.85em; color: #666;">Edad: ${esc.edad_evaluada || '-'}</span>
                    </td>
                  `
                }).join('')}
              </tr>
              <tr>
                <td style="font-weight: bold;">Haber Bruto</td>
                ${escs.map(esc => `
                  <td style="text-align: right; font-weight: bold; color: #003DA5;">
                    ${toCurrency(esc.haber_mensual ?? esc.haber?.haber_total)}
                  </td>
                `).join('')}
              </tr>
              <tr style="background-color: #f1f2f6;">
                <td style="font-weight: bold;">Descuentos</td>
                ${escs.map(esc => {
                  const cuota = esc.desglose_cuota_moratoria || 0
                  const os = esc.descuento_obra_social || 0
                  const total = cuota + os
                  return `
                    <td style="text-align: right; color: #dc2626; font-size: 0.9em;">
                      ${total > 0 ? `-${toCurrency(total)}` : '-'}
                      ${cuota > 0 ? '<br><span style="font-size: 0.8em; color: #888;">Cuota Moratoria (Ley 27.705): $' + fmtVal(VALOR_UPDP_ACTUAL) + '</span>' : ''}
                      ${os > 0 ? '<br><span style="font-size: 0.8em; color: #888;">Obra Social (3%): -' + toCurrency(os) + '</span>' : ''}
                    </td>
                  `
                }).join('')}
              </tr>
              <tr>
                <td style="font-weight: bold;">Haber Neto</td>
                ${escs.map(esc => {
                  const neto = Number(esc.haber_neto ?? esc.haber_mensual ?? 0)
                  const bruto = Number(esc.haber_mensual ?? 0)
                  const esMaximo = neto >= maxNeto && maxNeto > 0
                  const esRecomendado =
                    (escenarioRecomendadoId && (escenarioRecomendadoId === esc.id || escenarioRecomendadoId === esc.nombre)) ||
                    (escenarioRecomendado && escenarioRecomendado.includes(esc.nombre))
                  return `
                    <td style="text-align: right; font-weight: bold; ${esMaximo || esRecomendado ? 'color: #16a34a; background-color: #f0fdf4;' : 'color: #333;'}">
                      ${toCurrency(neto)}
                      ${esMaximo || esRecomendado ? ' <span style="color: #16a34a;">✓</span>' : ''}
                      ${bruto > 0 && neto !== bruto ? '<br><span style="font-size: 0.75em; color: #888; font-weight: normal;">Bruto ' + toCurrency(bruto) + ' - Desc. ' + toCurrency(bruto - neto) + '</span>' : ''}
                    </td>
                  `
                }).join('')}
              </tr>
              <tr style="background-color: #f8fff8;">
                <td colspan="${escs.length + 1}" style="text-align: center; font-size: 0.85em; color: #555; padding: 6px;">
                  <strong>Fórmula:</strong> Haber Neto = Haber Bruto − Obra Social (3%) − Cuota Moratoria (Ley 27.705 — UPDP: $${fmtVal(VALOR_UPDP_ACTUAL)})
                </td>
              </tr>
              <tr style="background-color: #f1f2f6;">
                <td style="font-weight: bold;">Tasa de Sustitución</td>
                ${escs.map(esc => {
                  const tasa = obtenerTasa(esc)
                  return `
                    <td style="text-align: right;">
                      ${fmtPct(tasa)}
                      ${esc.diagnostico?.alerta ? ' <span title="Tasa con riesgo de confiscatoriedad">⚠️</span>' : ''}
                    </td>
                  `
                }).join('')}
              </tr>
            </tbody>
          </table>

          <div style="margin-top: 15px; padding: 10px; background-color: #eff6ff; border-left: 4px solid #003DA5; border-radius: 4px;">
            <strong>Resumen Ejecutivo:</strong> ${reporteIntegral.resumen_ejecutivo?.mensaje_principal || ''}
            ${reporteIntegral.resumen_ejecutivo?.diferencia_porcentual_beneficio ? 
              `<br><strong>Diferencia de beneficio:</strong> ${reporteIntegral.resumen_ejecutivo.diferencia_porcentual_beneficio > 0 ? '+' : ''}${reporteIntegral.resumen_ejecutivo.diferencia_porcentual_beneficio}%` : ''}
          </div>
        </div>
        <div class="page-break"></div>
      `
    }

    // 3b. ANEXOS LEGALES: Gualtieri y Quiroga
    let anexosLegalesHTML = ''
    const activaQuiroga = escs.some(e => e.diagnostico?.esBaja || e.diagnostico?.alerta)
    const gualtieriFlags = escs.flatMap(e => e.haber?.gualtieriFlags || [])
    const gualtieriConDetalle = gualtieriFlags.length > 0
    const gualtieriGenerico = gualtieriFlags.length === 0 && activaQuiroga

    if (activaQuiroga || gualtieriConDetalle || gualtieriGenerico) {
      anexosLegalesHTML = `
        <div class="section" style="page-break-before: always;">
          <h2 class="section-title">ANEXOS TÉCNICO-JURÍDICOS</h2>
          <p style="font-size: 9.5pt; color: #444; margin-bottom: 12px;">
            Los siguientes anexos recogen los fundamentos doctrinarios aplicables al caso, 
            conforme la jurisprudencia vigente de la Corte Suprema de Justicia de la Nación.
          </p>
      `

      if (activaQuiroga) {
        anexosLegalesHTML += `
          <div class="annex-box">
            <div class="annex-title">Anexo I — Doctrina del Fallo "Quiroga, Estela María c/ANSES s/Reajustes Varios" (CSJN)</div>
            <p><b>Fundamento:</b> El derecho a la movilidad jubilatoria reconocido en el Art. 14 bis de la Constitución Nacional exige que el haber de retiro guarde una <b>proporción razonable y suficiente</b> respecto de las remuneraciones percibidas en actividad.</p>
            <p>La CSJN estableció en el precedente "Quiroga" que la tasa de sustitución del haber previsional no debe situarse por debajo del <b>umbral del 70%</b> respecto del promedio de las remuneraciones actualizadas. Cuando dicho umbral no se alcanza, se configura un supuesto de <b>confiscatoriedad</b> que vulnera el derecho de propiedad (Art. 17 CN) y el principio de proporcionalidad.</p>
            <p><b>Conclusión técnica:</b> En el presente caso se ha detectado una tasa de sustitución inferior al umbral del 70%, correspondiendo evaluar el planteo de inconstitucionalidad de la movilidad aplicada y reclamar el reajuste del haber conforme el índice combinado (Fallos "Blanco" y "Elliff").</p>
            <p class="cite">CSJN, "Quiroga, Estela María c/ANSES s/Reajustes Varios", Fallo 337:1177 (2014). Doctrina aplicable: Arts. 14 bis, 17 y 28 CN.</p>
          </div>
        `
      }

      if (gualtieriConDetalle) {
        const periodosAfectados = gualtieriFlags.map(f => f.periodo).filter(Boolean)
        anexosLegalesHTML += `
          <div class="annex-box">
            <div class="annex-title">Anexo II — Doctrina del Fallo "Gualtieri, Gabriel c/ANSES s/Reajustes Varios" (CSJN)</div>
            <p><b>Fundamento:</b> La aplicación de <b>topes máximos</b> sobre las remuneraciones imponibles (Art. 9 Ley 24.463) no puede reducir el haber jubilatorio por debajo de un nivel que resulte confiscatorio o irrazonable.</p>
            <p>La CSJN en "Gualtieri" reiteró que los topes previstos en el Art. 9 de la Ley 24.463 no deben aplicarse de manera mecánica cuando su efecto sea reducir la prestación por debajo del mínimo constitucionalmente protegido. Corresponde declarar la <b>inconstitucionalidad</b> de dicha limitación en el caso concreto.</p>
            <p><b>Periodos con tope detectados:</b> ${periodosAfectados.join(', ')}</p>
            <p><b>Cantidad de meses afectados:</b> ${gualtieriFlags.length}</p>
            <p class="cite">CSJN, "Gualtieri, Gabriel c/ANSES s/Reajustes Varios", Fallo 335:1645 (2012). Doctrina aplicable: Art. 9 Ley 24.463, Arts. 14 bis y 17 CN.</p>
          </div>
        `
      }

      if (gualtieriGenerico) {
        anexosLegalesHTML += `
          <div class="annex-box">
            <div class="annex-title">Anexo II — Doctrina del Fallo "Gualtieri, Gabriel c/ANSES s/Reajustes Varios" (CSJN)</div>
            <p><b>Fundamento:</b> La aplicación de <b>topes máximos</b> sobre las remuneraciones imponibles (Art. 9 Ley 24.463) no puede reducir el haber jubilatorio por debajo de un nivel que resulte confiscatorio o irrazonable.</p>
            <p>La CSJN en "Gualtieri" reiteró que los topes previstos en el Art. 9 de la Ley 24.463 no deben aplicarse de manera mecánica cuando su efecto sea reducir la prestación por debajo del mínimo constitucionalmente protegido. La tasa de sustitución obtenida en el presente caso sugiere que la aplicación de topes máximos pudo haber afectado el haber final, correspondiendo evaluar la procedencia de su declaración de inconstitucionalidad.</p>
            <p><b>Observación:</b> No se detectaron meses específicos con exceso de tope en los registros disponibles. Sin perjuicio de ello, la doctrina sentada por la CSJN en el precedente "Gualtieri" resulta aplicable cuando el haber final se ve reducido por la aplicación mecánica de los topes del Art. 9 de la Ley 24.463, correspondiendo su revisión oficiosa por el tribunal interviniente.</p>
            <p class="cite">CSJN, "Gualtieri, Gabriel c/ANSES s/Reajustes Varios", Fallo 335:1645 (2012). Doctrina aplicable: Art. 9 Ley 24.463, Arts. 14 bis y 17 CN.</p>
          </div>
        `
      }

      anexosLegalesHTML += `
        </div>
        <div class="page-break"></div>
      `
    }

    let tablaRemuneracionesHTML = ''
    if (remuneracionesTabla.length > 0) {
      tablaRemuneracionesHTML = `
        <div class="section">
          <h2 class="section-title">SENTINEL V3 - REMUNERACIONES EXTRAIDAS</h2>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Periodo</th>
                <th>Monto</th>
                <th>Caja</th>
                <th>Coeficiente</th>
                <th>Actualizado</th>
              </tr>
            </thead>
            <tbody>
              ${remuneracionesTabla.map((r, idx) => `
                <tr>
                  <td>${idx + 1}</td>
                  <td>${r.periodo || '-'}</td>
                  <td>${toCurrency(r.remOriginal ?? r.monto ?? 0)}</td>
                  <td>${r.caja || 'SIPA'}${(r.caja && r.caja !== 'SIPA') ? ' ⚠️' : ''}</td>
                  <td>${Number(r.coeficiente ?? 1).toFixed(4)}</td>
                  <td>${toCurrency(r.valorFinal ?? r.monto ?? 0)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `
    }

    // 4. TABLA DE APORTES (from historiaLaboral)
    let tablaAportesHTML = ''
    if (historiaLaboral?.periodos?.length > 0) {
      const periodos = [...historiaLaboral.periodos].sort((a, b) => 
        new Date(a.fechaDesde) - new Date(b.fechaDesde)
      )
      
      // Get remuneraciones if available
      const remuneraciones = datos.remuneraciones || []
      
      tablaAportesHTML = `
        <div class="section">
          <h2 class="section-title">HISTORIA LABORAL</h2>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Empleador</th>
                <th>CUIT</th>
                <th>Desde</th>
                <th>Hasta</th>
                <th>Tipo</th>
                <th>Años</th>
                <th>Remuneración Total</th>
                <th>Computable</th>
              </tr>
            </thead>
            <tbody>
              ${periodos.map((p, idx) => {
                const emp = p.empresaNombre || `Periodo ${idx + 1}`
                const cuit = p.cuit || 'No disponible'
                const desde = new Date(p.fechaDesde).toLocaleDateString('es-AR')
                const hasta = new Date(p.fechaHasta).toLocaleDateString('es-AR')
                const tipo = p.tipoActividad || 'No especificado'
                const dias = (new Date(p.fechaHasta) - new Date(p.fechaDesde)) / (1000 * 60 * 60 * 24)
                const anos = ((dias / 365.25) * 100 | 0) / 100
                
                // Calculate total remuneration for this period
                const desdeDate = new Date(p.fechaDesde)
                const hastaDate = new Date(p.fechaHasta)
                const periodRemuneraciones = remuneraciones.filter(r => {
                  return r.fecha >= desdeDate && r.fecha <= hastaDate
                })
                const totalRemuneracion = periodRemuneraciones.reduce((sum, r) => sum + (r.monto || 0), 0)
                
                const computable = (p.tipoActividad !== 'desempleo' && p.aportePorcentaje > 0) ? 'Sí' : 'No'
                const bgColor = computable === 'Sí' ? '' : 'style="background-color: #ffe6e6;"'
                
                return `
                  <tr ${bgColor}>
                    <td>${idx + 1}</td>
                    <td>${emp}</td>
                    <td>${cuit}</td>
                    <td>${desde}</td>
                    <td>${hasta}</td>
                    <td>${tipo}</td>
                    <td>${anos.toFixed(2)}</td>
                    <td>$${totalRemuneracion.toLocaleString('es-AR')}</td>
                    <td>${computable}</td>
                  </tr>
                `
              }).join('')}
            </tbody>
          </table>
        </div>
      `
      
      // Page break if > 15 records
      if (periodos.length > 15) {
        tablaAportesHTML += '<div class="page-break"></div>'
      }
    }

    // 5. DOCTRINA APLICADA
    let doctrinaAplicada = 'Se ha procedido a la actualización mediante Índice Combinado '
    doctrinaAplicada += '(Fallo Blanco/Elliff) para garantizar la sustitutividad del salario. '
    doctrinaAplicada += 'Verificación de Fallo Gualtieri (Art. 9 Ley 24.241) para topes máximos.'
    
    if (reporteIntegral?.metadata?.version_motor) {
      doctrinaAplicada += ` Motor: ${reporteIntegral.metadata.version_motor}.`
    }

    // 5b. METODOLOGIA Y AUDITORIA DE VENTANA DE CALCULO
    let auditoriaHTML = ''
    if (auditoriaCalculo) {
      const periodoDesde = auditoriaCalculo.periodoDesde || 'No disponible'
      const periodoHasta = auditoriaCalculo.periodoHasta || 'No disponible'
      const cantidad = Number(auditoriaCalculo.cantidadMesesConAportes || 0)
      const calidadEntrada = datos.calidadEntrada || null
      const cobertura = Number(calidadEntrada?.cobertura || 0)
      const metodologia = auditoriaCalculo.metodologia ||
        'Calculo realizado sobre los ultimos 120 meses con aportes efectivos, excluyendo periodos de inactividad o registros en cero.'
      const nota = auditoriaCalculo.nota ||
        'Los periodos sin aportes registrados (monto $0) han sido omitidos para no afectar el promedio de remuneraciones.'

      auditoriaHTML = `
        <div class="section">
          <h3>METODOLOGIA DE CALCULO Y AUDITORIA DE DATOS</h3>
          <p style="font-size: 12px;"><b>Periodo de referencia:</b> ${periodoDesde} hasta ${periodoHasta}.</p>
          <p style="font-size: 12px;"><b>Densidad de aportes:</b> Se han detectado ${cantidad} meses con remuneraciones positivas dentro del historial analizado.</p>
          ${calidadEntrada ? `<p style="font-size: 12px;"><b>Calidad de entrada:</b> ${cobertura}% de cobertura utilizable (${calidadEntrada.totalValidas || 0} validas / ${calidadEntrada.totalExtraidas || 0} extraidas).</p>` : ''}
          <p style="font-size: 12px;"><b>Base del promedio:</b> ${metodologia}</p>
          ${auditoriaCalculo.alerta ? `<p style="font-size: 11px; color: #9a3412; background: #fff7ed; border-left: 4px solid #ea580c; padding: 8px;"><b>Advertencia de densidad:</b> ${auditoriaCalculo.alerta}</p>` : ''}
          <p style="font-size: 11px; color: #57606f;"><i>Nota: ${nota}</i></p>
        </div>
      `

      const ai = datos.auditoriaIntegridad || null
      if (ai && Number.isFinite(Number(ai.tasaSustitucion))) {
        auditoriaHTML += `
          <div class="section" style="margin-top: 8px;">
            <h3>CONSISTENCIA PERICIAL</h3>
            <p style="font-size: 12px;">Se ha verificado la razonabilidad del haber resultante mediante tasa de sustitutividad, arrojando un coeficiente del ${Number(ai.tasaSustitucion || 0).toFixed(2)}%, dentro de los parametros de doctrina CSJN (Fallo Betancur).</p>
            ${ai.alerta ? `<p style="font-size: 11px; color: #9a3412; background: #fff7ed; border-left: 4px solid #ea580c; padding: 8px;"><b>Alerta de consistencia:</b> ${ai.alerta.mensaje}</p>` : '<p style="font-size: 11px; color: #137333; background: #ecfdf3; border-left: 4px solid #16a34a; padding: 8px;"><b>Resultado:</b> Calculo consistente dentro de umbrales esperados.</p>'}
          </div>
        `
      }
    }

    // 6. CONCLUSIÓN
    let conclusion = ''
    if (reporteIntegral?.resumen_ejecutivo?.mensaje_principal) {
      conclusion = reporteIntegral.resumen_ejecutivo.mensaje_principal
    } else {
      conclusion = `Corresponde el otorgamiento de la prestación con un haber mensual estimado de $${fmtVal(haberTotal)}.`
    }
    
    if (tasaSustitucion < 70) {
      conclusion += ' SE DETECTA CONFISCATORIEDAD (Tasa < 70%). Corresponde ajuste por movilidad.'
    }

    const hashFuente = datos.hashPDF || datos.archivoHash || hashRapido({
      cuil,
      rems: remuneracionesTabla.map((r) => `${r.periodo || ''}:${r.remOriginal || r.monto || 0}`)
    })
    const fechaTrazabilidad = `${new Date().toLocaleString('es-AR', { hour12: false })} ART`

    const cajaOtorgante = analisisMulticaja?.cajaOtorgante || juridico?.cajaJubilacion || 'SIPA'
    const estiloCaja = STYLES_CAJA[cajaOtorgante] || STYLES_CAJA.OTRA

    const headerInstitucional = `
      <div style="border-left: 8px solid ${estiloCaja.bg}; padding-left: 20px; margin: 14px 0 20px;">
        <h1 style="margin: 0; font-size: 20px; color: ${estiloCaja.bg};">DICTAMEN PERICIAL PREVISIONAL</h1>
        <div style="display: flex; gap: 10px; align-items: center; margin-top: 6px; line-height: 1.25;">
          <span style="background: ${estiloCaja.bg}; color: #fff; padding: 4px 10px; border-radius: 4px; font-size: 11px; font-weight: bold; line-height: 1.2;">
            CAJA OTORGANTE: ${estiloCaja.label}
          </span>
          ${analisisMulticaja?.esHibrido ? '<span style="border: 1px solid #ffa000; color: #9a3412; background: #fff7ed; padding: 2px 7px; border-radius: 4px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 700;">⚠️ SISTEMA HIBRIDO</span>' : ''}
        </div>
      </div>
    `

    let seccionReciprocidad = ''
    let glosaReciprocidad = ''
    if (analisisMulticaja?.esHibrido) {
      seccionReciprocidad = `
        <div class="section" style="background: #fff3e0; border-left: 5px solid #ff9800;">
          <strong>NOTA TECNICA - RECIPROCIDAD PREVISIONAL:</strong>
          <p style="font-size: 11px; margin-top: 6px;">
            Se detectaron aportes en multiples cajas. Segun el Decreto-Ley 9316/46,
            la caja otorgante es <strong>${cajaOtorgante}</strong> por mayor tiempo de servicios.
            Se recomienda iniciar Reconocimiento de Servicios en las cajas no otorgantes.
          </p>
        </div>
      `
      glosaReciprocidad = `
        <div class="section" style="background: #fffdf5; border-left: 5px solid #ffa000; page-break-inside: avoid;">
          <strong>Fundamento Juridico de Reciprocidad:</strong>
          <p style="font-size: 11px; margin-top: 6px;">
            Conforme al Decreto-Ley 9316/46 y convenios de reciprocidad vigentes,
            se determina que la caja otorgante del beneficio es <strong>${cajaOtorgante}</strong>.
            Se advierte la necesidad de gestionar formularios de Reconocimiento de Servicios
            (Resolucion 524/08) ante las cajas no otorgantes para asegurar el computo total
            de la antiguedad laboral.
          </p>
        </div>
      `
    }

    // 7. REEMPLAZAR PLACEHOLDERS EN TEMPLATE
    template = template.replace(/\{\{NOMBRE_TITULAR\}\}/g, nombre)
    template = template.replace(/\{\{CUIL\}\}/g, cuil)
    template = template.replace(/\{\{DNI\}\}/g, dni)
    template = template.replace(/\{\{FECHA_CALCULO\}\}/g, fechaCalculo)
    template = template.replace(/\{\{FECHA_CESE\}\}/g, fechaCese)
    template = template.replace(/\{\{EDAD_CIERRE\}\}/g, edadCierre)
    
    template = template.replace(/\{\{HABER_TOTAL\}\}/g, fmtVal(haberTotal))
    template = template.replace(/\{\{PROMEDIO_120\}\}/g, fmtVal(promedio120))
    template = template.replace(/\{\{TOTAL_AÑOS\}\}/g, totalAnos)
    template = template.replace(/\{\{TASA_SUSTITUCION\}\}/g, tasaSustitucion.toFixed(2))
    
    template = template.replace(/\{\{AÑOS_PRE_94\}\}/g, Number(anosPre94).toFixed(2))
    template = template.replace(/\{\{AÑOS_POST_94\}\}/g, Number(anosPost94).toFixed(2))
    template = template.replace(/\{\{PBU_VALOR\}\}/g, fmtVal(pbu))
    template = template.replace(/\{\{PC_VALOR\}\}/g, fmtVal(pc))
    template = template.replace(/\{\{PAP_VALOR\}\}/g, fmtVal(pap))
    
    template = template.replace(/\{\{DOCTRINA_APLICADA\}\}/g, doctrinaAplicada)
    template = template.replace(/\{\{CONCLUSION\}\}/g, conclusion)
    
    // NUEVO: Insertar tabla comparativa de escenarios
    template = template.replace(/\{\{TABLA_ESCENARIOS\}\}/g, tablaEscenariosHTML)
    template = template.replace(/\{\{SECCION_AUDITORIA\}\}/g, auditoriaHTML)
    template = template.replace(/\{\{TABLA_REMUNERACIONES\}\}/g, tablaRemuneracionesHTML)
    template = template.replace(/\{\{TABLA_APORTES\}\}/g, tablaAportesHTML)
    template = template.replace(/\{\{ANEXOS_LEGALES\}\}/g, anexosLegalesHTML)
    template = template.replace(/\{\{MOTOR_VERSION\}\}/g, 'V3-Sentinel-Gold')
    template = template.replace(/\{\{HASH_PDF\}\}/g, hashFuente)
    template = template.replace(/\{\{FECHA_TRAZABILIDAD\}\}/g, fechaTrazabilidad)
    template = template.replace(/\{\{CAJA_COLOR\}\}/g, estiloCaja.bg)
    template = template.replace(/\{\{CAJA_TIPO\}\}/g, estiloCaja.text)
    template = template.replace(/\{\{CAJA_LABEL\}\}/g, estiloCaja.label)
    template = template.replace(/\{\{HEADER_INSTITUCIONAL\}\}/g, headerInstitucional)
    template = template.replace(/\{\{SECCION_RECIPROCIDAD\}\}/g, seccionReciprocidad)
    template = template.replace(/\{\{GLOSA_RECIPROCIDAD\}\}/g, glosaReciprocidad)
    
    // Apply highlighting to PC and PAP rows if promedio120 > 0
    if (promedio120 > 0) {
        template = template.replace(
            '<tr><td>PC (Prestación Compensatoria)</td><td>1.5% x',
            '<tr style="background-color: #fff9c4;"><td>PC (Prestación Compensatoria)</td><td>1.5% x'
        );
        template = template.replace(
            '<tr><td>PAP (Prestación Adicional)</td><td>1.5% x',
            '<tr style="background-color: #fff9c4;"><td>PAP (Prestación Adicional)</td><td>1.5% x'
        );
    }

    return template

  } catch (error) {
    console.error('Error generando reporte HTML:', error)
    return ''
  }
}

/**
 * Fallback: Generate legacy HTML if template v3 not found
 */
function generarHTMLLegacy(datos) {
  const { juridico, economico, historiaLaboral } = datos
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Reporte Previsional</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        table { width: 100%; border-collapse: collapse; margin: 15px 0; }
        th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
        th { background-color: #f0f0f0; }
      </style>
    </head>
    <body>
      <h1>Reporte Previsional</h1>
      <p><strong>Haber Estimado:</strong> $${economico?.haberTotal?.toFixed(2) || 0}</p>
      <p><strong>Régimen:</strong> ${juridico?.regimen || 'No determinado'}</p>
      <p><strong>Aportes Totales:</strong> ${juridico?.aportesTotales?.toFixed(2) || 0} años</p>
    </body>
    </html>
  `
}

export default generarReporteHTML
